import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AspectRatio, BreakPoint } from "types/ui";
import { useMinWidthMediaQuery } from "hooks/useMediaQuery";
import { Image } from "@caspeco/casper-ui-library.components.image";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { GridWithDividers } from "components/grid-with-dividers/GridWithDividers";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { AspectRatioWrapper } from "components/aspect-ratio-wrapper/AspectRatioWrapper";
import { createBSTLSearchParams } from "helpers/search-param-helper/search-param-helper";
import { usePickSearchParams } from "hooks/usePickSearchParams";
import { useMenuAPI, useMenuStore } from "store/menuStore";
import { Section } from "types/section";
import NotFound from "components/not-found/NotFound";

export function MenuSectionList() {
	const { t } = useTranslation();
	const { deliveryOptionType, deliveryOptionId } = usePickSearchParams([
		"deliveryOptionType",
		"deliveryOptionId",
	]);
	const [, setSearchParams] = useSearchParams();
	const isLargerThanLargeBreakPoint = useMinWidthMediaQuery(BreakPoint.Large);
	const isLargerThanXLargeBreakPoint = useMinWidthMediaQuery(BreakPoint.XLarge);
	const { menu } = useMenuStore();
	const { setSelectedSection } = useMenuAPI();

	if (!menu) return null;

	if (!menu.sections.length) {
		return <NotFound description={t("menu_section_articles_empty_title")} />;
	}

	const onClose = (): void => {
		setSearchParams(createBSTLSearchParams(deliveryOptionType, deliveryOptionId, false));
	};

	const renderItem = (section: Section): JSX.Element => {
		const { imageMap, name } = section;

		const onClick = () => {
			setSelectedSection(section);
			onClose();
		};

		return (
			<button
				type="button"
				onClick={onClick}
				style={{ height: "100%" }}
				aria-label={`${t("action_show")} ${name}`}
			>
				<Flex direction="column" justifyContent="space-between" minH="100%" gap={ThemeSpaceVariable.Small}>
					{imageMap.hasImage(AspectRatio.OneByOne) && (
						<Image
							alt={t("misc_image_of_x", { 0: name })}
							borderRadius={ThemeRadiusVariable.Medium}
							src={imageMap.withSizeQuery(AspectRatio.OneByOne, "l")}
							srcSet={imageMap.getScrSet(AspectRatio.OneByOne, "l", "xl")}
							fallback={
								<AspectRatioWrapper $aspectratio={AspectRatio.OneByOne}>
									<Box
										bg={ThemeColorVariable.BackgroundDisabled}
										borderRadius={ThemeRadiusVariable.Medium}
										w="100%"
									/>
								</AspectRatioWrapper>
							}
						/>
					)}
					{/* If no image exists, show empty box taking up same space image would */}
					{!imageMap.hasImage(AspectRatio.OneByOne) && (
						<AspectRatioWrapper $aspectratio={AspectRatio.OneByOne}>
							<Box minW="100%" />
						</AspectRatioWrapper>
					)}
					<Heading
						as="h2"
						fontFamily={ThemeFontVariable.SubHeader}
						fontSize={{
							base: ThemeFontSizeVariable.Medium,
							sm: ThemeFontSizeVariable.XLarge,
						}}
					>
						{name}
					</Heading>
				</Flex>
			</button>
		);
	};

	let columnCount = 2;
	if (isLargerThanLargeBreakPoint) columnCount = 3;
	if (isLargerThanXLargeBreakPoint) columnCount = 4;

	return (
		<Box p={ThemeSpaceVariable.Medium} h="100%">
			<GridWithDividers columnCount={columnCount} renderGridItem={renderItem} items={menu.sections} />
		</Box>
	);
}
